import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../utils/constants';

const REIFY_LOGO = 'https://customer-assets.emergentagent.com/job_dbd68d1b-28e6-42d0-902a-82a09fb5a2e9/artifacts/65npxqdg_atdzxq91_ChatGPT%20Image%20Mar%2018%2C%202026%2C%2008_54_02%20AM.png';

const AuthenticationSystem = ({ onLogin }) => {
  const resetToken = new URLSearchParams(window.location.search).get('token');
  const isResetRoute = window.location.pathname === '/reset-password' && !!resetToken;
  const [authMode, setAuthMode] = useState(isResetRoute ? 'reset-password' : 'login'); // 'login', 'forgot-password', 'reset-password'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const switchMode = (mode) => {
    setError('');
    setSuccessMessage('');
    if (mode === 'login' && isResetRoute) window.history.replaceState({}, '', '/');
    setAuthMode(mode);
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-cyan-700/10 border border-cyan-500/20 backdrop-blur-sm mb-5 shadow-lg shadow-cyan-500/10">
            <img src={REIFY_LOGO} alt="Reify Systems" className="h-12 w-12 object-contain" data-testid="reify-logo" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-widest mb-1 uppercase">REIFY SYSTEMS</h1>
          <p className="text-gray-500 text-sm tracking-wide">Intelligent Construction Operating System</p>
        </div>

        {/* Authentication Form */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl p-8">
          {authMode === 'login' && (
            <LoginForm 
              onLogin={onLogin}
              onSwitchMode={switchMode}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              error={error}
              setError={setError}
              successMessage={successMessage}
            />
          )}
          
          {authMode === 'forgot-password' && (
            <ForgotPasswordForm
              onSwitchMode={switchMode}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              error={error}
              setError={setError}
              successMessage={successMessage}
              setSuccessMessage={setSuccessMessage}
            />
          )}

          {authMode === 'reset-password' && (
            <ResetPasswordForm
              token={resetToken}
              onSwitchMode={switchMode}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              error={error}
              setError={setError}
              setSuccessMessage={setSuccessMessage}
            />
          )}
        </div>

        </div>
      </div>
  );
};

// Login Form Component
const LoginForm = ({ onLogin, onSwitchMode, isLoading, setIsLoading, error, setError, successMessage }) => {
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });

  const handleInputChange = (e) => {
    setCredentials(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setError(''); // Clear error when user starts typing
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/login`, credentials);
      console.log('Login response:', response.data);
      
      if (response.data.access_token) {
        // Store token in localStorage
        localStorage.setItem('authToken', response.data.access_token);
        localStorage.setItem('tokenExpiry', Date.now() + (8 * 60 * 60 * 1000)); // 8 hours
        
        // Create user object with permissions based on role
        const userData = response.data.user;
        const userWithPermissions = {
          id: userData.id,
          email: userData.email,
          name: userData.full_name,
          role: userData.role === 'admin' ? 'Admin/Manager' : 'Field Staff',
          permissions: {
            canEdit: userData.role === 'admin',
            canViewFinancials: userData.role === 'admin',
            canManageUsers: userData.role === 'admin',
            canUploadPhotos: true,
            canViewJobs: true
          },
          rawRole: userData.role,
          allowedGears: userData.allowed_gears || null,
          isActive: userData.is_active,
          isLocked: userData.is_locked,
          companyId: userData.company_id || '',
          isPlatformAdmin: userData.is_platform_admin || false,
          mustChangePassword: userData.must_change_password || false,
          company: userData.company || null,
        };
        
        console.log('Calling onLogin with:', userWithPermissions);
        onLogin(userWithPermissions);
      }
    } catch (err) {
      console.error('Login error:', err);
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string' && detail) {
        setError(detail);
      } else if (!err.response || err.response.status >= 500) {
        setError('The server is temporarily unavailable. Please try again in a few minutes.');
      } else {
        setError('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleLogin} className="space-y-6" data-testid="login-form">
      <h2 className="text-2xl font-bold text-center text-white mb-6">
        Sign In
      </h2>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm" data-testid="login-error">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-4 py-3 rounded-lg text-sm" data-testid="login-success">
          {successMessage}
        </div>
      )}

      <div>
        <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-2">
          Email Address
        </label>
        <input
          type="email"
          name="email"
          value={credentials.email}
          onChange={handleInputChange}
          className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          placeholder="Enter your email address"
          required
          disabled={isLoading}
          data-testid="login-email-input"
        />
      </div>

      <div>
        <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-2">
          Password
        </label>
        <input
          type="password"
          name="password"
          value={credentials.password}
          onChange={handleInputChange}
          className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          placeholder="Enter your password"
          required
          disabled={isLoading}
          data-testid="login-password-input"
        />
      </div>

      <button
        type="submit"
        disabled={isLoading || !credentials.email || !credentials.password}
        className="w-full bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 disabled:text-gray-500 text-white py-3 rounded-lg font-bold transition-colors"
        data-testid="login-submit-btn"
      >
        {isLoading ? 'Signing In...' : 'Sign In'}
      </button>

      <div className="text-center space-y-2">
        <button
          type="button"
          onClick={() => onSwitchMode('forgot-password')}
          className="text-cyan-400 hover:text-cyan-300 text-sm"
          disabled={isLoading}
          data-testid="forgot-password-link"
        >
          Forgot your password?
        </button>
      </div>


    </form>
  );
};

// Forgot Password Form Component
const ForgotPasswordForm = ({ onSwitchMode, isLoading, setIsLoading, error, setError, successMessage, setSuccessMessage }) => {
  const [email, setEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccessMessage('Password reset email sent! Check your email for instructions.');
      setEmail('');
    } catch (err) {
      console.error('Forgot password error:', err);
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Failed to send password reset email. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h2 className="text-2xl font-bold text-center text-white mb-6">
        Reset Password
      </h2>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-4 py-3 rounded-lg text-sm">
          {successMessage}
        </div>
      )}

      <div>
        <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-2">
          Email Address
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          placeholder="Enter your email address"
          required
          disabled={isLoading}
        />
        <p className="text-gray-500 text-sm mt-1">
          We'll send you instructions to reset your password.
        </p>
      </div>

      <button
        type="submit"
        disabled={isLoading || !email}
        className="w-full bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 disabled:text-gray-500 text-white py-3 rounded-lg font-bold transition-colors"
      >
        {isLoading ? 'Sending...' : 'Send Reset Email'}
      </button>

      <div className="text-center">
        <button
          type="button"
          onClick={() => onSwitchMode('login')}
          className="text-cyan-400 hover:text-cyan-300 text-sm"
          disabled={isLoading}
        >
          Back to Sign In
        </button>
      </div>
    </form>
  );
};

// Reset Password Form Component (reached via /reset-password?token=...)
const ResetPasswordForm = ({ token, onSwitchMode, isLoading, setIsLoading, error, setError, setSuccessMessage }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setSuccessMessage('Your password has been reset. You can now sign in.');
      onSwitchMode('login');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' && detail ? detail : 'Unable to reset password. Please request a new link.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass = "w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500";

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
      <h2 className="text-2xl font-bold text-center text-white mb-6">Choose a New Password</h2>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm" data-testid="reset-password-error">
          {error}
        </div>
      )}

      <div>
        <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-2">New Password</label>
        <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={inputClass}
          placeholder="At least 8 characters, upper, lower and a number" required disabled={isLoading} data-testid="reset-password-input" />
      </div>
      <div>
        <label className="block text-gray-400 text-xs uppercase tracking-wider font-medium mb-2">Confirm Password</label>
        <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={inputClass}
          placeholder="Re-enter your new password" required disabled={isLoading} data-testid="reset-password-confirm-input" />
      </div>

      <button type="submit" disabled={isLoading || !newPassword || !confirmPassword}
        className="w-full bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 disabled:text-gray-500 text-white py-3 rounded-lg font-bold transition-colors"
        data-testid="reset-password-submit-btn">
        {isLoading ? 'Saving...' : 'Set New Password'}
      </button>

      <div className="text-center">
        <button type="button" onClick={() => onSwitchMode('login')} className="text-cyan-400 hover:text-cyan-300 text-sm" disabled={isLoading} data-testid="reset-back-to-login">
          Back to Sign In
        </button>
      </div>
    </form>
  );
};

// Company Registration Page removed — companies are now provisioned by platform admin only

export { AuthenticationSystem };
